import { Box, Tooltip } from '@mui/material';
import { T, Icon, stitchTooltipSlotProps } from '../ProjectOverview/parts/stitch';
import { STATUS_STYLES } from '../../config/projectFlow';
import { TEKST, eerlijkeFasen, faseLabel } from './rustigeKop';

/**
 * De fasenbalk als rustige stappenbalk (Joris, 25 sep 2026): alleen tekst —
 * afgeronde fasen grijs met vinkje, de actieve donker en onderstreept, de rest
 * lichtgrijs. Uitgezette fasen staan er niet in (de balk wordt korter — dát is
 * de flow van dit project); uitzetten kan met het kruisje bij hover,
 * terugzetten via het instellingen-icoon (PhaseSettingsDialog).
 * Een vinkje alleen als de fase én alle fasen ervóór af zijn (eerlijkeFasen):
 * een losse 'klaar' verderop heet in de tooltip "Deels".
 */
function PhaseRail({ phases, activeKey, onSelect, onDisable, onOpenSettings, driftCount = 0 }) {
  const zichtbaar = eerlijkeFasen(phases).filter((p) => p.is_enabled);
  return (
    <Box component="nav" aria-label="Fasen"
      sx={{ display: 'flex', alignItems: 'center', columnGap: 2.5, rowGap: 1, flexWrap: 'wrap',
        borderBottom: `1px solid ${T.surfaceHigh}` }}>
      {zichtbaar.map((p) => {
        const stijl = STATUS_STYLES[p.status] || STATUS_STYLES.todo;
        const actief = p.phase_key === activeKey;
        const klaar = p.status === 'klaar';
        const kleur = actief ? T.text : klaar ? T.outline : p.status === 'bezig' ? T.secondary : T.outlineVariant;
        return (
          <Box key={p.id} sx={{ position: 'relative', display: 'flex', alignItems: 'center',
            '&:hover .fase-weg': { opacity: 1 } }}>
            <Tooltip slotProps={stitchTooltipSlotProps} describeChild
              title={`${faseLabel(p, stijl.label)}${p.status_override ? ' (handmatig)' : ''} — ${[
                ...(p.evidence || []), ...(p.deels ? ['een eerdere fase is nog niet af'] : []),
              ].join(' · ') || p.status_source}`}>
              <Box component="button" type="button" onClick={() => onSelect(p.phase_key)}
                aria-current={actief ? 'step' : undefined}
                sx={{
                  border: 0, bgcolor: 'transparent', cursor: 'pointer', px: 0, pt: 0.5, pb: 1, mb: '-1px',
                  display: 'inline-flex', alignItems: 'center', gap: 0.4,
                  fontFamily: T.fontBody, fontSize: TEKST, whiteSpace: 'nowrap',
                  fontWeight: actief ? 700 : 500, color: kleur,
                  borderBottom: `2px solid ${actief ? T.text : 'transparent'}`,
                  '&:hover': { color: T.text },
                }}>
                {klaar && <Box component="span" aria-hidden sx={{ display: 'inline-flex' }}><Icon name="check" size={14} /></Box>}
                {p.label}
              </Box>
            </Tooltip>
            {/* Fase weg-klikken (omkeerbaar via het instellingen-icoon) */}
            {zichtbaar.length > 1 && (
              <Tooltip title="Fase uitzetten voor dit project (terugzetten kan via de fase-instellingen)" slotProps={stitchTooltipSlotProps} describeChild>
                <Box component="button" type="button" className="fase-weg" aria-label={`${p.label} uitzetten`}
                  onClick={(e) => { e.stopPropagation(); onDisable(p); }}
                  sx={{
                    position: 'absolute', top: -8, right: -12, width: 16, height: 16, p: 0,
                    borderRadius: '50%', border: 0, bgcolor: T.surface, cursor: 'pointer',
                    display: 'grid', placeItems: 'center', opacity: 0, transition: 'opacity 0.15s ease',
                    color: T.outlineVariant, '&:hover': { color: T.error }, '&:focus-visible': { opacity: 1 },
                  }}>
                  <Icon name="close" size={12} />
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      })}
      <Box sx={{ flex: 1 }} />
      <Tooltip slotProps={stitchTooltipSlotProps} describeChild
        title={`Fasen aanpassen${driftCount ? ` — ${driftCount} nieuwe fase beschikbaar` : ''}`}>
        <Box component="button" type="button" onClick={onOpenSettings} aria-label="Fasen aanpassen"
          sx={{ border: 0, bgcolor: 'transparent', p: 0, pb: 1, cursor: 'pointer', color: T.outlineVariant,
            display: 'inline-flex', alignItems: 'center', gap: 0.4, fontFamily: T.fontBody, fontSize: TEKST,
            '&:hover': { color: T.text } }}>
          <Icon name="tune" size={16} />
          {driftCount > 0 && <Box component="span" sx={{ color: T.secondary }}>{driftCount} nieuw</Box>}
        </Box>
      </Tooltip>
    </Box>
  );
}

export default PhaseRail;
